import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CalendarPicker } from '@mui/x-date-pickers/CalendarPicker';
import { format, isValid, parseISO, startOfDay } from 'date-fns';

function parseYmd(value) {
  if (!value) return null;
  const parsed = parseISO(String(value));
  return isValid(parsed) ? startOfDay(parsed) : null;
}

function toYmd(date) {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd');
}

function formatDisplayRange(from, to) {
  if (!from && !to) return '';
  const fromLabel = from ? format(from, 'MMM dd, yyyy') : 'Start';
  const toLabel = to ? format(to, 'MMM dd, yyyy') : 'End';
  return `${fromLabel} – ${toLabel}`;
}

/**
 * Single input that opens a dual-calendar popover for date_from / date_to.
 */
export default function DateRangeFilterField({
  dateFrom = '',
  dateTo = '',
  onChange,
  label = 'Date range',
  fullWidth = true,
}) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(null);
  const [draftTo, setDraftTo] = useState(null);

  const appliedFrom = useMemo(() => parseYmd(dateFrom), [dateFrom]);
  const appliedTo = useMemo(() => parseYmd(dateTo), [dateTo]);
  const displayValue = formatDisplayRange(appliedFrom, appliedTo);
  const hasValue = Boolean(dateFrom || dateTo);

  const openPicker = () => {
    setDraftFrom(appliedFrom);
    setDraftTo(appliedTo);
    setOpen(true);
  };

  const closePicker = () => setOpen(false);

  const emitChange = (fromDate, toDate) => {
    if (typeof onChange === 'function') {
      onChange({
        date_from: toYmd(fromDate),
        date_to: toYmd(toDate),
      });
    }
  };

  const handleFromSelect = (date) => {
    const nextFrom = date ? startOfDay(date) : null;
    let nextTo = draftTo;
    if (nextFrom && nextTo && nextTo < nextFrom) {
      nextTo = null;
    }
    setDraftFrom(nextFrom);
    setDraftTo(nextTo);
  };

  const handleToSelect = (date) => {
    const nextTo = date ? startOfDay(date) : null;
    if (draftFrom && nextTo && nextTo < draftFrom) {
      setDraftTo(draftFrom);
      setDraftFrom(nextTo);
      return;
    }
    setDraftTo(nextTo);
  };

  const handleApply = () => {
    emitChange(draftFrom, draftTo);
    closePicker();
  };

  const handleClear = (event) => {
    event.stopPropagation();
    setDraftFrom(null);
    setDraftTo(null);
    emitChange(null, null);
    closePicker();
  };

  return (
    <>
      <TextField
        inputRef={anchorRef}
        fullWidth={fullWidth}
        label={label}
        value={displayValue}
        placeholder="Select date range"
        onClick={openPicker}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          readOnly: true,
          style: { cursor: 'pointer' },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: hasValue ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear date range"
                onClick={handleClear}
                edge="end"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            borderRadius: 2,
            boxShadow: 6,
            overflow: 'hidden',
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Box sx={{ minWidth: 280 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, px: 1 }}>
                From
              </Typography>
              <CalendarPicker
                date={draftFrom}
                onChange={handleFromSelect}
                maxDate={draftTo || undefined}
              />
            </Box>
            <Box
              sx={{
                width: { xs: '100%', sm: '1px' },
                height: { xs: '1px', sm: 'auto' },
                bgcolor: 'divider',
                alignSelf: 'stretch',
              }}
            />
            <Box sx={{ minWidth: 280 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, px: 1 }}>
                To
              </Typography>
              <CalendarPicker
                date={draftTo}
                onChange={handleToSelect}
                minDate={draftFrom || undefined}
              />
            </Box>
          </Box>
        </LocalizationProvider>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mt: 1,
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {formatDisplayRange(draftFrom, draftTo) || 'Select start and end dates'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={handleClear} color="inherit">
              Clear
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
